import { createRoot } from "react-dom/client";
import { registerSW } from "virtual:pwa-register";
import App from "./App.tsx";
import "./index.css";
import { captureAttributionFromUrl } from "@/lib/attribution";
import { trackSessionLanding } from "@/lib/analytics";

const originalLandingPath = window.location.pathname;
const originalLandingQuery = window.location.search;
captureAttributionFromUrl();
trackSessionLanding(originalLandingPath, originalLandingQuery);

const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const PREVIEW_REFRESH_PARAM = "__preview_fresh";
const PREVIEW_RESUME_PARAM = "__preview_resume";
const PREVIEW_PURGE_STATE_PARAM = "__preview_purge_state";
const PREVIEW_HIDDEN_AT_KEY = "greyrock_preview_hidden_at";
const PREVIEW_RESUME_RELOAD_AT_KEY = "greyrock_preview_resume_reloaded_at";
const PREVIEW_BOOTSTRAP_RELOAD_AT_KEY = "greyrock_preview_bootstrap_reloaded_at";
const PREVIEW_IDLE_RESET_MS = 2 * 60 * 1000;
const AUTH_STORAGE_KEY_PATTERNS = [/^sb-/i, /supabase/i];
const PREVIEW_SAFE_PATHS = new Set([
  "/",
  "/practice",
  "/rehearsal",
  "/rehearsal/debrief",
  "/archetypes",
  "/dashboard",
  "/admin",
  "/login",
  "/reset-password",
  "/legal",
  "/privacy",
  "/terms",
  "/refunds",
  "/billing-fresh",
  "/analysis-result",
  "/vault",
  "/reports",
  "/neutralizer",
  "/trends",
  "/settings",
  "/system-admin",
  "/admin-portal",
  "/admin/content-engine",
  "/admin/content-library",
]);

const renderApp = () => {
  createRoot(rootElement).render(<App />);
};

const isLovablePreviewHost = () => {
  const host = window.location.hostname;
  return host.endsWith(".lovableproject.com") || (host.includes("-preview--") && host.endsWith(".lovable.app"));
};

const isRecoveryFlow = () => {
  const fullUrl = window.location.href;
  const hashParams = window.location.hash;
  return hashParams.includes("type=recovery") || fullUrl.includes("type=recovery");
};

const isKnownAppPath = (pathname: string) => {
  if (PREVIEW_SAFE_PATHS.has(pathname)) return true;
  if (/^\/rehearsal\/debrief\/[^/]+$/.test(pathname)) return true;
  return /^\/neutralizer-lab\/[^/]+$/.test(pathname);
};

const replacePreviewWithRoot = (reason: "invalid" | "resume") => {
  const targetUrl = new URL(window.location.origin + "/");
  targetUrl.searchParams.set(PREVIEW_RESUME_PARAM, reason);
  window.location.replace(targetUrl.toString());
};

const schedulePreviewHardResetReload = () => {
  const url = new URL(window.location.href);
  url.searchParams.set(PREVIEW_PURGE_STATE_PARAM, "armed");
  url.searchParams.set(PREVIEW_REFRESH_PARAM, String(Date.now()));
  window.location.replace(url.toString());
};

const shouldPreserveAuthStorageKey = (key: string) => AUTH_STORAGE_KEY_PATTERNS.some((pattern) => pattern.test(key));

const snapshotStorageEntries = (storage: Storage, shouldKeep: (key: string) => boolean) => {
  const entries: Array<[string, string]> = [];

  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (!key || !shouldKeep(key)) continue;

    const value = storage.getItem(key);
    if (value !== null) {
      entries.push([key, value]);
    }
  }

  return entries;
};

const restoreStorageEntries = (storage: Storage, entries: Array<[string, string]>) => {
  entries.forEach(([key, value]) => storage.setItem(key, value));
};

const forcePreviewRefresh = (reason: "resume") => {
  const url = new URL(window.location.href);
  url.searchParams.set(PREVIEW_RESUME_PARAM, reason);
  url.searchParams.set(PREVIEW_REFRESH_PARAM, String(Date.now()));
  sessionStorage.setItem(PREVIEW_RESUME_RELOAD_AT_KEY, String(Date.now()));
  window.location.replace(url.toString());
};

const forcePreviewBootstrapRefresh = () => {
  const url = new URL(window.location.href);
  url.searchParams.set(PREVIEW_RESUME_PARAM, "resume");
  url.searchParams.set(PREVIEW_REFRESH_PARAM, String(Date.now()));
  sessionStorage.setItem(PREVIEW_BOOTSTRAP_RELOAD_AT_KEY, String(Date.now()));
  window.location.replace(url.toString());
};

const cleanupPreviewParams = () => {
  const url = new URL(window.location.href);
  const hadRefreshParam = url.searchParams.has(PREVIEW_REFRESH_PARAM);
  const hadResumeParam = url.searchParams.has(PREVIEW_RESUME_PARAM);
  const hadPurgeParam = url.searchParams.has(PREVIEW_PURGE_STATE_PARAM);

  if (!hadRefreshParam && !hadResumeParam && !hadPurgeParam) return;

  url.searchParams.delete(PREVIEW_REFRESH_PARAM);
  url.searchParams.delete(PREVIEW_RESUME_PARAM);
  url.searchParams.delete(PREVIEW_PURGE_STATE_PARAM);
  sessionStorage.removeItem(PREVIEW_RESUME_RELOAD_AT_KEY);
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
};

const shouldSchedulePreviewHardReset = () => {
  if (!isLovablePreviewHost() || isRecoveryFlow()) return false;

  const url = new URL(window.location.href);
  return !url.searchParams.has(PREVIEW_PURGE_STATE_PARAM);
};

const performPreviewHardResetIfNeeded = async () => {
  if (!isLovablePreviewHost() || isRecoveryFlow()) return false;

  const url = new URL(window.location.href);
  if (url.searchParams.get(PREVIEW_PURGE_STATE_PARAM) !== "armed") return false;

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch (error) {
    console.warn("Preview hard reset skipped service worker cleanup:", error);
  }

  try {
    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }
  } catch (error) {
    console.warn("Preview hard reset skipped cache cleanup:", error);
  }

  try {
    const preservedLocalEntries = snapshotStorageEntries(localStorage, shouldPreserveAuthStorageKey);
    localStorage.clear();
    restoreStorageEntries(localStorage, preservedLocalEntries);
    sessionStorage.clear();
  } catch (error) {
    console.warn("Preview hard reset skipped storage cleanup:", error);
  }

  url.searchParams.set(PREVIEW_PURGE_STATE_PARAM, "done");
  url.searchParams.set(PREVIEW_REFRESH_PARAM, String(Date.now()));
  window.location.replace(url.toString());
  return true;
};

const shouldForcePreviewBootstrapRefresh = () => {
  if (!isLovablePreviewHost() || isRecoveryFlow()) return false;

  const url = new URL(window.location.href);
  if (url.searchParams.has(PREVIEW_REFRESH_PARAM) || url.searchParams.has(PREVIEW_RESUME_PARAM)) {
    return false;
  }

  const lastBootstrapReloadAt = Number(sessionStorage.getItem(PREVIEW_BOOTSTRAP_RELOAD_AT_KEY) || "0");
  return lastBootstrapReloadAt === 0;
};

const shouldResetPreviewRouteOnBootstrap = () => {
  if (!isLovablePreviewHost() || isRecoveryFlow()) return false;

  const url = new URL(window.location.href);
  const pathname = url.pathname;

  if (url.searchParams.has(PREVIEW_REFRESH_PARAM) || url.searchParams.has(PREVIEW_RESUME_PARAM)) {
    return false;
  }

  if (!isKnownAppPath(pathname)) {
    return true;
  }

  const hiddenAt = Number(sessionStorage.getItem(PREVIEW_HIDDEN_AT_KEY) || "0");
  const wasIdleTooLong = hiddenAt > 0 && Date.now() - hiddenAt >= PREVIEW_IDLE_RESET_MS;
  const shouldResetIdleRoute = wasIdleTooLong && pathname !== "/";

  return shouldResetIdleRoute;
};

const installPreviewResumeRecovery = () => {
  if (!isLovablePreviewHost()) return;

  const markHidden = () => {
    sessionStorage.setItem(PREVIEW_HIDDEN_AT_KEY, String(Date.now()));
  };

  const recoverIfNeeded = (force = false) => {
    if (isRecoveryFlow()) return;

    const hiddenAt = Number(sessionStorage.getItem(PREVIEW_HIDDEN_AT_KEY) || "0");
    const lastResumeReloadAt = Number(sessionStorage.getItem(PREVIEW_RESUME_RELOAD_AT_KEY) || "0");
    const wasIdleTooLong = hiddenAt > 0 && Date.now() - hiddenAt >= PREVIEW_IDLE_RESET_MS;
    const pathname = window.location.pathname;
    const hasInvalidPath = !isKnownAppPath(pathname);
    const resumedAfterBeingHidden = hiddenAt > 0;
    const shouldRefreshCurrentPage = force || wasIdleTooLong || resumedAfterBeingHidden;
    const alreadyReloadedThisResume = lastResumeReloadAt >= hiddenAt && lastResumeReloadAt > 0;

    if (hasInvalidPath) {
      replacePreviewWithRoot("invalid");
      return;
    }

    if (wasIdleTooLong && pathname === "/dashboard") {
      replacePreviewWithRoot("resume");
      return;
    }

    if (shouldRefreshCurrentPage && !alreadyReloadedThisResume) {
      forcePreviewRefresh("resume");
    }
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      markHidden();
      return;
    }

    recoverIfNeeded();
  });

  window.addEventListener("pagehide", markHidden);
  window.addEventListener("pageshow", (event) => {
    recoverIfNeeded((event as PageTransitionEvent).persisted);
  });
};

const handleRecoveryBootstrap = () => {
  const hashParams = window.location.hash;

  if (isRecoveryFlow()) {
    sessionStorage.setItem("password_recovery_pending", "true");
    if (hashParams) {
      sessionStorage.setItem("password_recovery_hash", hashParams);
    }

    if (window.location.pathname !== "/reset-password") {
      window.location.replace(`/reset-password${window.location.hash}`);
      return;
    }
  }

  renderApp();
};

const clearStaleCaches = async () => {
  const url = new URL(window.location.href);
  const wasReloadedForFreshness = url.searchParams.has(PREVIEW_REFRESH_PARAM);

  let hadRegistrations = false;
  let hadCaches = false;

  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      hadRegistrations = registrations.length > 0;
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch (error) {
    console.warn("Service worker cleanup skipped:", error);
  }

  try {
    if ("caches" in window) {
      const cacheKeys = await caches.keys();
      hadCaches = cacheKeys.length > 0;
      await Promise.all(cacheKeys.map((key) => caches.delete(key)));
    }
  } catch (error) {
    console.warn("Cache cleanup skipped:", error);
  }

  if ((hadRegistrations || hadCaches) && !wasReloadedForFreshness) {
    const refreshedUrl = new URL(window.location.href);
    refreshedUrl.searchParams.set(PREVIEW_REFRESH_PARAM, "1");
    window.location.replace(refreshedUrl.toString());
    return true;
  }

  cleanupPreviewParams();
  return false;
};

const shouldRegisterPwa = () => {
  if (isLovablePreviewHost()) return false;
  return window.location.protocol === "https:" || window.location.hostname === "localhost";
};

const registerPwaIfEligible = () => {
  if (!shouldRegisterPwa()) return;
  registerSW({
    immediate: true,
    onRegisterError(error) {
      console.warn("PWA registration error:", error);
    },
  });
};

const bootstrap = async () => {
  if (shouldSchedulePreviewHardReset()) {
    schedulePreviewHardResetReload();
    return;
  }

  const hardResetReloaded = await performPreviewHardResetIfNeeded();
  if (hardResetReloaded) return;

  if (shouldForcePreviewBootstrapRefresh()) {
    forcePreviewBootstrapRefresh();
    return;
  }

  if (shouldResetPreviewRouteOnBootstrap()) {
    replacePreviewWithRoot(isKnownAppPath(window.location.pathname) ? "resume" : "invalid");
    return;
  }

  const reloaded = await clearStaleCaches();
  if (reloaded) return;

  installPreviewResumeRecovery();
  registerPwaIfEligible();
  handleRecoveryBootstrap();
};

void bootstrap();